import { useEffect, useCallback, useRef, memo, useMemo } from 'react'
import InlineFieldEditor from './InlineFieldEditor'

const isEmptyValue = (val) => {
  if (val === undefined || val === null) return true
  if (val === '') return true
  if (Array.isArray(val) && val.length === 0) return true
  if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true
  return false
}

const getFieldValue = (viewMode, draftStates, block, fieldName) => {
  if (viewMode === 'draft') {
    const draftVal = draftStates[block.id]?.[fieldName]
    const revisionVal = block.latest_revision?.content?.[fieldName]
    const baseVal = block.content?.[fieldName]

    if (draftStates[block.id] && fieldName in draftStates[block.id]) {
      return draftVal
    }

    if (block.latest_revision?.content && fieldName in block.latest_revision.content && !isEmptyValue(revisionVal)) {
      return revisionVal
    }

    return baseVal
  }

  return block.content?.[fieldName]
}

const FieldWrapper = memo(
  function FieldWrapper({
    field,
    block,
    viewMode,
    draftStates,
    locale,
    onFieldChange,
    readOnly = false,
  }) {
    const onFieldChangeRef = useRef(onFieldChange)

    useEffect(() => {
      onFieldChangeRef.current = onFieldChange
    }, [onFieldChange])

    const currentValue = useMemo(() => {
      return getFieldValue(viewMode, draftStates, block, field.name)
    }, [viewMode, draftStates, block, field.name])

    const handleChange = useCallback(
      (value) => {
        if (!readOnly) {
          onFieldChangeRef.current(field.name, value)
        }
      },
      [field.name, readOnly]
    )

    return (
      <div>
        <InlineFieldEditor
          field={field}
          value={currentValue}
          onChange={handleChange}
          isEditable={viewMode === 'draft' && !readOnly}
          locale={locale}
          blockId={block.id}
          blockTypeId={block.block_type_id}
          readOnly={readOnly}
        />
      </div>
    )
  },
  (prevProps, nextProps) => {
    const prevValue = getFieldValue(
      prevProps.viewMode,
      prevProps.draftStates,
      prevProps.block,
      prevProps.field.name
    )
    const nextValue = getFieldValue(
      nextProps.viewMode,
      nextProps.draftStates,
      nextProps.block,
      nextProps.field.name
    )

    return (
      prevProps.field.id === nextProps.field.id &&
      prevProps.block.id === nextProps.block.id &&
      prevProps.viewMode === nextProps.viewMode &&
      prevProps.locale === nextProps.locale &&
      prevProps.readOnly === nextProps.readOnly &&
      JSON.stringify(prevValue) === JSON.stringify(nextValue)
    )
  }
)

export default FieldWrapper
